import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { ApiListEnvelop, getEmptyApiListEnvelop } from '@mkp/shared/models';
import { QueryOptions, toQueryString } from '@mkp/shared/util-pagination';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

interface HttpOptions {
  headers?: HttpHeaders | Record<string, string | string[]>;
  context?: HttpContext;
  observe?: 'body';
  params?:
    HttpParams | Record<string, string | number | boolean | readonly (string | number | boolean)[]>;
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
  transferCache?: boolean | { includeHeaders?: string[] };
}

/**
 * @deprecated
 * Use BaseHttpResource (libs/shared/data-access) instead
 */
export interface Serializer {
  fromJson(json: unknown): unknown;
  toJson(resource: unknown): unknown;
}

/**
 * @deprecated
 * Use BaseHttpResource (libs/shared/data-access) instead
 */
export class ResourceService<T> {
  constructor(
    public httpClient: HttpClient,
    public url: string,
    public endpoint: string,
    public serializer?: Serializer
  ) {}

  public create(item: T): Observable<T> {
    return this.httpClient
      .post<T>(
        `${this.url}/${this.endpoint}`,
        this.serializer ? this.serializer.toJson(item as T) : (item as T)
      )
      .pipe(map((data) => (this.serializer ? (this.serializer.fromJson(data) as T) : (data as T))));
  }

  // FIXME not working on change status. Request no reading JSON
  public createRead(id: string, item: unknown): Observable<T> {
    return this.httpClient.post<T[]>(`${this.url}/${this.endpoint}/${id}`, new Object(item)).pipe(
      map(([data]: unknown[]) => {
        // [FIXME TO UPDATE ON BULK ACTIONS] need to be an array of ids
        return this.serializer ? (this.serializer.fromJson(data) as T) : (data as T);
      })
    );
  }

  public createList(item: T): Observable<T> {
    // TODO serialize this
    return this.httpClient.post<T>(`${this.url}/${this.endpoint}`, item).pipe(map((data) => data));
  }

  public updatePutMethod(item: Partial<T> & { id: string }): Observable<T> {
    return this.httpClient
      .put<T>(
        `${this.url}/${this.endpoint}/${item.id}`,
        this.serializer ? this.serializer.toJson(item) : (item as Partial<T>)
      )
      .pipe(map((data) => (this.serializer ? (this.serializer.fromJson(data) as T) : (data as T))));
  }

  public patchValue<T>(item: Record<string, unknown>, queryOptions?: QueryOptions): Observable<T> {
    const params = this.getQueryParams(queryOptions);
    const options = this.getOptions(queryOptions);

    const url = `${this.url}/${this.endpoint}/${String(item['id'])}${params}`;
    return this.httpClient
      .patch<T>(url, item, options)
      .pipe(map((data) => (this.serializer ? (this.serializer.fromJson(data) as T) : (data as T))));
  }

  public read(id: string): Observable<T> {
    return this.httpClient
      .get(`${this.url}/${this.endpoint}/${id}`)
      .pipe(map((data) => (this.serializer ? (this.serializer.fromJson(data) as T) : (data as T))));
  }

  public readArray(id: string): Observable<T> {
    // TODO serialize this
    return this.httpClient
      .get<T>(`${this.url}/${this.endpoint}/${id}`)
      .pipe(map((data: T) => data));
  }

  public uniq(): Observable<T> {
    return this.httpClient
      .get(`${this.url}/${this.endpoint}`)
      .pipe(map((data) => (this.serializer ? (this.serializer.fromJson(data) as T) : (data as T))));
  }

  public list(queryOptions?: QueryOptions, withAccountId?: string): Observable<ApiListEnvelop<T>> {
    const params = this.getQueryParams(queryOptions);
    const options = this.getOptions(queryOptions);

    const url = withAccountId
      ? `${this.url}/account/${withAccountId}/${this.endpoint}`
      : `${this.url}/${this.endpoint}`;

    return this.httpClient
      .get<ApiListEnvelop<T>>(`${url}${params}`, options)
      .pipe(
        catchError((error: unknown) =>
          queryOptions?.propagateError ? throwError(() => error) : of(getEmptyApiListEnvelop<T>())
        )
      );
  }

  public listProxy(queryOptions?: QueryOptions): Observable<T[]> {
    const params = this.getQueryParams(queryOptions);
    const options = this.getOptions(queryOptions);

    return this.httpClient
      .get<T[]>(`${this.url}/${this.endpoint}${params}`, options)
      .pipe(
        catchError((error: unknown) =>
          queryOptions?.propagateError ? throwError(() => error) : of([])
        )
      );
  }

  public delete(id: string) {
    return this.httpClient.delete(`${this.url}/${this.endpoint}/${id}`);
  }

  private getQueryParams(queryOptions?: QueryOptions): string {
    const queryString = queryOptions ? toQueryString(queryOptions) : '';
    return queryString ? `?${queryString}` : '';
  }

  private getOptions(queryOptions?: QueryOptions): HttpOptions {
    // the maxRetry header will be intercepted and removed before reaching the API
    return queryOptions?.maxRetry != null
      ? ({
          headers: new HttpHeaders({ maxRetry: `${queryOptions.maxRetry}` }),
        } as unknown as HttpOptions)
      : {};
  }
}
